import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RefreshCw, Trash2, Calculator, X, Info } from "lucide-react";
import AdminSideBar from "./AdminSideBar";
import { authFetch } from "../utils/auth";
import UserProfileMenu from "./UserProfileMenu";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());
// Gray out a zero value so the rows that actually cost money stand out.
const dimZero = (v, activeCls = "") => ((Number(v) || 0) === 0 ? "text-gray-300" : activeCls);
const fmtUsd = (n) => (n == null ? "—" : `$${Number(n).toFixed(4)}`);
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    + " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

// Compute summary totals from already-aggregated by-user/by-api-key rows (one row
// per user/key, full unlimited backend GROUP BY — unlike the session log, which is
// capped at limit=500 and would silently undercount once total sessions exceed that).
const sumAggRows = (aggRows) => ({
  total_cost_usd: aggRows.reduce((s, r) => s + (r.cost_usd || 0), 0),
  total_savings_usd: aggRows.reduce((s, r) => s + (r.savings_usd || 0), 0),
  total_sessions: aggRows.reduce((s, r) => s + (r.sessions || 0), 0),
  total_turns: aggRows.reduce((s, r) => s + (r.turns || 0), 0),
  total_input_tokens: aggRows.reduce((s, r) => s + (r.input_tokens || 0), 0),
  total_output_tokens: aggRows.reduce((s, r) => s + (r.output_tokens || 0), 0),
  total_cache_write_tokens: aggRows.reduce((s, r) => s + (r.cache_write_tokens || 0), 0),
  total_cache_read_tokens: aggRows.reduce((s, r) => s + (r.cache_read_tokens || 0), 0),
});

// ── Cost Calculator ─────────────────────────────────────────────────────────
const CALC_MODELS = {
  "claude-opus-5-5":   { input: 4.0,  cache_write: 5.0,  cache_read: 0.2,  output: 20.0 },
  "claude-opus-4-6":   { input: 5.0,  cache_write: 6.25, cache_read: 0.5,  output: 25.0 },
  "claude-sonnet-4-6": { input: 3.0,  cache_write: 3.75, cache_read: 0.3,  output: 15.0 },
  "claude-haiku-4-5":  { input: 1.0,  cache_write: 1.25, cache_read: 0.1,  output: 5.0  },
};

const AdminTokenUsagePage = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Top-level view mode: frontend users vs API keys
  const [viewMode, setViewMode] = useState("user"); // "user" | "apikey"

  // Raw data from backend
  const [byUser, setByUser] = useState([]);       // frontend-only, from get_usage_by_user
  const [byApiKey, setByApiKey] = useState([]);   // API-only, from get_usage_by_api_key
  const [rows, setRows] = useState([]);            // all sessions combined
  const [userIdMap, setUserIdMap] = useState({});
  const [keepalive, setKeepalive] = useState({ cost_usd: 0, hits: 0, misses: 0 }); // prompt-cache keepalive (global)

  // Per-mode selection filters (client-side only — no re-fetch needed)
  const [filterUser, setFilterUser] = useState("");          // selected username (user mode)
  const [filterApiKeyName, setFilterApiKeyName] = useState(""); // selected key name (apikey mode)

  // Date range — changing these triggers a re-fetch
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  // UI state
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState("");
  const [rotateLoading, setRotateLoading] = useState(false);
  const [rotateMsg, setRotateMsg] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
  // "How are these calculated?" legend, collapsed by default
  const [showLegend, setShowLegend] = useState(false);
  // Per-session turn rows, lazily loaded from /turns, keyed by session_id
  const [turnsBySession, setTurnsBySession] = useState({});
  const [turnsLoading, setTurnsLoading] = useState({});
  // Session Log sorting
  const [sortKey, setSortKey] = useState("started_at");
  const [sortDir, setSortDir] = useState("desc");
  // Cost Calculator state
  const [showCalc, setShowCalc] = useState(false);
  const [calcModel, setCalcModel] = useState("claude-sonnet-4-6");
  const [calcRates, setCalcRates] = useState({ ...CALC_MODELS["claude-sonnet-4-6"] });
  const [calcTokens, setCalcTokens] = useState({ input: "", cache_write: "", cache_read: "", output: "" });
  // Model rates come from the model_pricing db table (via /pricing); CALC_MODELS
  // is only an offline fallback if that fetch fails.
  const [calcModels, setCalcModels] = useState(CALC_MODELS);

  const handleCalcModelChange = (model) => setCalcModel(model);

  // Keep the selected model valid and its rates in sync with the loaded pricing.
  // Fires on model switch or when db pricing arrives — not on manual rate edits.
  useEffect(() => {
    const keys = Object.keys(calcModels);
    if (!keys.length) return;
    const active = calcModels[calcModel] ? calcModel : keys[0];
    if (active !== calcModel) setCalcModel(active);
    const r = calcModels[active];
    setCalcRates({
      input: r.input, cache_write: r.cache_write, cache_read: r.cache_read, output: r.output,
    });
  }, [calcModels, calcModel]);
  const calcCost = useMemo(() => {
    const inp = parseFloat(calcTokens.input) || 0;
    const cw  = parseFloat(calcTokens.cache_write) || 0;
    const cr  = parseFloat(calcTokens.cache_read) || 0;
    const out = parseFloat(calcTokens.output) || 0;
    return {
      inp_cost: inp * calcRates.input / 1e6,
      cw_cost:  cw  * calcRates.cache_write / 1e6,
      cr_cost:  cr  * calcRates.cache_read  / 1e6,
      out_cost: out * calcRates.output / 1e6,
      total:    (inp * calcRates.input + cw * calcRates.cache_write + cr * calcRates.cache_read + out * calcRates.output) / 1e6,
      without_cache: (inp + cw + cr) * calcRates.input / 1e6 + out * calcRates.output / 1e6,
    };
  }, [calcTokens, calcRates]);

  const toggleExpand = (id) => setExpandedRows(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // Lazily fetch a session's individual turns, cached per session.
  const fetchTurns = useCallback(async (sessionId) => {
    if (!sessionId || turnsBySession[sessionId] || turnsLoading[sessionId]) return;
    setTurnsLoading(prev => ({ ...prev, [sessionId]: true }));
    try {
      const res = await authFetch(`/api/admin/token-usage-logs/turns?session_id=${encodeURIComponent(sessionId)}`);
      if (res.ok) {
        const data = await res.json();
        setTurnsBySession(prev => ({ ...prev, [sessionId]: data.turns || [] }));
      }
    } catch { /* ignore — aggregate totals still show */ }
    finally {
      setTurnsLoading(prev => ({ ...prev, [sessionId]: false }));
    }
  }, [turnsBySession, turnsLoading]);

  const handleRowToggle = (sessionId) => {
    toggleExpand(sessionId);
    fetchTurns(sessionId);
  };

  // Load turns for any expanded session (covers rows opened before data arrived).
  useEffect(() => {
    expandedRows.forEach(sid => fetchTurns(sid));
  }, [expandedRows, fetchTurns]);

  // Session Log column sorting
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  const sortArrow = (key) => (sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : "");

  // fetchData only re-fetches when date range changes; user/key selection is purely client-side
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    // Drop cached per-turn rows so expanded sessions re-fetch them (the
    // expandedRows effect below). Without this Refresh updated the session
    // totals but left an open breakdown frozen at its first load.
    setTurnsBySession({});
    try {
      const params = new URLSearchParams();
      if (filterFrom) params.set("from_date", new Date(filterFrom).toISOString());
      if (filterTo) params.set("to_date", new Date(filterTo + "T23:59:59").toISOString());
      const qs = params.toString() ? `?${params.toString()}` : "";
      const sep = qs ? "&" : "?";

      const [byUserRes, byApiKeyRes, rowsRes, keepaliveRes, usersRes, pricingRes] = await Promise.all([
        authFetch(`/api/admin/token-usage-logs/by-user${qs}`),
        authFetch(`/api/admin/token-usage-logs/by-api-key${qs}`),
        authFetch(`/api/admin/token-usage-logs/by-session${qs}${sep}limit=500`),
        authFetch(`/api/admin/token-usage-logs/keepalive${qs}`),
        authFetch("/api/admin/users"),
        authFetch("/api/admin/token-usage-logs/pricing"),
      ]);

      if (!byUserRes.ok) throw new Error("Failed to fetch per-user data.");
      if (!byApiKeyRes.ok) throw new Error("Failed to fetch per-API-key data.");
      if (!rowsRes.ok) throw new Error("Failed to fetch session log.");

      const [byUserData, byApiKeyData, rowsData] = await Promise.all([
        byUserRes.json(),
        byApiKeyRes.json(),
        rowsRes.json(),
      ]);

      if (keepaliveRes.ok) {
        const ka = await keepaliveRes.json();
        setKeepalive({ cost_usd: ka.cost_usd || 0, hits: ka.hits || 0, misses: ka.misses || 0 });
      }

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        const map = {};
        for (const u of (usersData.users || [])) {
          if (u.user_id) map[u.user_id] = u.username;
          if (u.sub) map[u.sub] = u.username;
        }
        setUserIdMap(map);
      }

      // Cost Calculator rates from the model_pricing db table
      if (pricingRes.ok) {
        const p = await pricingRes.json();
        if (p.models && Object.keys(p.models).length) setCalcModels(p.models);
      }

      setByUser(byUserData.users || []);
      setByApiKey(byApiKeyData.api_keys || []);
      setRows(rowsData.sessions || []);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message || "Failed to load token usage data.");
    } finally {
      setLoading(false);
    }
  }, [filterFrom, filterTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Default the From/To filters to the actual range of fetched data (oldest to
  // today) once the initial unfiltered load completes, so it's visible up front
  // where the logs start — still fully editable by the user afterward.
  useEffect(() => {
    if (filterFrom || filterTo || !rows.length) return;
    const oldest = rows[rows.length - 1]?.started_at;
    if (!oldest) return;
    setFilterFrom(new Date(oldest).toISOString().slice(0, 10));
    setFilterTo(new Date().toISOString().slice(0, 10));
  }, [rows, filterFrom, filterTo]);

  // Split all session rows by source (frontend = no api_key_name, API = has api_key_name)
  const frontendRows = useMemo(() => rows.filter(r => !r.api_key_name), [rows]);
  const apiRows = useMemo(() => rows.filter(r => r.api_key_name && r.api_key_name !== "__frontend__"), [rows]);

  // Active session rows for the current mode + selection
  const activeSessionRows = useMemo(() => {
    if (viewMode === "user") {
      return filterUser ? frontendRows.filter(r => r.username === filterUser) : frontendRows;
    }
    return filterApiKeyName ? apiRows.filter(r => r.api_key_name === filterApiKeyName) : apiRows;
  }, [viewMode, filterUser, filterApiKeyName, frontendRows, apiRows]);

  // Session Log rows sorted by the clicked column
  const sortedSessionRows = useMemo(() => {
    const val = (r) => {
      switch (sortKey) {
        case "user": return (r.api_key_name || r.username || "").toLowerCase();
        case "session_id": return r.session_id || "";
        case "turns": return r.turns || 0;
        case "model": return (r.model || "").toLowerCase();
        case "cost_usd": return r.cost_usd || 0;
        case "savings_usd": return r.savings_usd || 0;
        case "started_at":
        default: return r.started_at || "";
      }
    };
    const dir = sortDir === "asc" ? 1 : -1;
    return [...activeSessionRows].sort((a, b) => {
      const av = val(a), bv = val(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [activeSessionRows, sortKey, sortDir]);

  // Summary cards reflect the current selection — sourced from the full, unlimited
  // by-user/by-api-key aggregations (not the capped session log) so totals always
  // match the table below, even once total sessions exceed the log's limit=500.
  const displaySummary = useMemo(() => {
    if (viewMode === "user") {
      const relevant = filterUser ? byUser.filter(r => r.username === filterUser) : byUser;
      return sumAggRows(relevant);
    }
    const relevant = filterApiKeyName ? byApiKey.filter(r => r.api_key_name === filterApiKeyName) : byApiKey;
    return sumAggRows(relevant);
  }, [viewMode, filterUser, filterApiKeyName, byUser, byApiKey]);

  // Keepalive is a global infra cost (not a token-usage row) — fold into Total
  // Cost only in the unfiltered view.
  const isFiltered = !!(filterUser || filterApiKeyName);
  const grandCost = useMemo(
    () => (displaySummary.total_cost_usd || 0) + (isFiltered ? 0 : (keepalive.cost_usd || 0)),
    [displaySummary.total_cost_usd, isFiltered, keepalive.cost_usd]
  );

  // Breakdown table rows — show selected row only when a filter is active
  const displayByUser = useMemo(
    () => (filterUser ? byUser.filter(u => u.username === filterUser) : byUser),
    [byUser, filterUser]
  );
  const displayByApiKey = useMemo(
    () => (filterApiKeyName ? byApiKey.filter(k => k.api_key_name === filterApiKeyName) : byApiKey),
    [byApiKey, filterApiKeyName]
  );

  const handleRotate = async (days) => {
    if (!window.confirm(`Delete all token usage records older than ${days} days?`)) return;
    setRotateLoading(true);
    setRotateMsg("");
    try {
      const res = await authFetch(`/api/admin/token-usage-logs/rotate?days=${days}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to rotate data.");
      const data = await res.json();
      setRotateMsg(`Deleted ${data.deleted} records older than ${days} days.`);
      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setRotateLoading(false);
    }
  };

  const switchMode = (mode) => {
    setViewMode(mode);
    setFilterUser("");
    setFilterApiKeyName("");
    setExpandedRows(new Set());
  };

  // Shared session log table (renders activeSessionRows for the current mode)
  const renderSessionLog = () => {
    const colCount = viewMode === "apikey" ? 10 : 9;
    return (
      <>
      <div className="border border-[#EBECE6] rounded-xl overflow-x-auto max-h-[70vh] overflow-y-auto bg-white shadow-sm [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {activeSessionRows.length === 0 ? (
          <div className="text-center py-12 text-gray-400">No sessions found.</div>
        ) : (
          <table className="w-full text-sm min-w-[900px] tabular-nums">
            <thead>
              <tr className="bg-[#f6f6f1] text-left text-gray-600 [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-[#f6f6f1]">
                <th className="px-4 py-3 font-semibold w-6"></th>
                <th className="px-4 py-3 font-semibold cursor-pointer select-none hover:text-gray-800" onClick={() => toggleSort("started_at")}>Started{sortArrow("started_at")}</th>
                {viewMode === "apikey" && <th className="px-4 py-3 font-semibold">API Key ID</th>}
                <th className="px-4 py-3 font-semibold cursor-pointer select-none hover:text-gray-800" onClick={() => toggleSort("user")}>{viewMode === "apikey" ? "API Key Name" : "User"}{sortArrow("user")}</th>
                <th className="px-4 py-3 font-semibold cursor-pointer select-none hover:text-gray-800" onClick={() => toggleSort("session_id")}>Session{sortArrow("session_id")}</th>
                <th className="px-4 py-3 font-semibold text-right cursor-pointer select-none hover:text-gray-800" onClick={() => toggleSort("turns")}>Turns{sortArrow("turns")}</th>
                <th className="px-4 py-3 font-semibold cursor-pointer select-none hover:text-gray-800" onClick={() => toggleSort("model")}>Model{sortArrow("model")}</th>
                <th className="px-4 py-3 font-semibold">Mode</th>
                <th className="px-4 py-3 font-semibold text-right cursor-pointer select-none hover:text-gray-800" onClick={() => toggleSort("cost_usd")}>Cost{sortArrow("cost_usd")}</th>
                <th className="px-4 py-3 font-semibold text-right cursor-pointer select-none hover:text-gray-800" onClick={() => toggleSort("savings_usd")}>Savings{sortArrow("savings_usd")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedSessionRows.map((r, i) => {
                const isExpanded = expandedRows.has(r.session_id);
                const displayUser = r.owner_user_id
                  ? (userIdMap[r.owner_user_id] || r.owner_user_id.slice(0, 8) + "…")
                  : r.username;
                const rateTitle = r.rate_input != null
                  ? `Rates ($/M): input=$${r.rate_input} cache_write=$${r.rate_cache_write} cache_read=$${r.rate_cache_read} output=$${r.rate_output}`
                  : "Rate snapshot not available";
                return (
                  <React.Fragment key={r.session_id}>
                    <tr
                      className={`border-t border-[#EBECE6] cursor-pointer hover:bg-gray-50 ${i % 2 === 0 ? "bg-white" : "bg-[#FAFAF6]"}`}
                      onClick={() => handleRowToggle(r.session_id)}
                    >
                      <td className="px-3 py-2 text-gray-400 text-center select-none">{isExpanded ? "▾" : "▸"}</td>
                      <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{fmtDate(r.started_at)}</td>
                      {viewMode === "apikey" && (
                        <td className="px-4 py-2 font-mono text-xs text-gray-500" title={r.api_key_id || ""}>{r.api_key_id || "—"}</td>
                      )}
                      <td className="px-4 py-2">
                        {viewMode === "apikey" ? (r.api_key_name || "—") : displayUser}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-400" title={r.session_id}>
                        {r.session_id ? r.session_id.slice(-12) : "—"}
                      </td>
                      <td className="px-4 py-2 text-right">{r.turns}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{r.model}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{r.source === "api_batch" ? "Batch" : "Copilot"}</td>
                      <td className={`px-4 py-2 text-right font-semibold cursor-help ${dimZero(r.cost_usd)}`} title={rateTitle}>{fmtUsd(r.cost_usd)}</td>
                      <td className={`px-4 py-2 text-right ${dimZero(r.savings_usd, "text-green-600")}`}>{fmtUsd(r.savings_usd)}</td>
                    </tr>
                    <tr className={(isExpanded || turnsBySession[r.session_id]) ? "bg-[#FAFAF6]" : ""}>
                      <td colSpan={colCount} className="p-0 border-0">
                        <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                          <div className="overflow-hidden">
                            {(isExpanded || turnsBySession[r.session_id]) && (
                          <div className="px-6 py-5">
                          {/* Per-turn detail — the highlighted row above is the session summary */}
                          <div className="flex items-center flex-wrap gap-2 mb-3">
                            <span className="text-[11px] font-bold text-[#253439] uppercase tracking-wide">Per-turn breakdown</span>
                            {Number(r.system_prompt_tokens) > 0 && (
                              <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-[#5b6b6a] bg-white border border-[#EBECE6] rounded-full px-2.5 py-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                                System prompt <span className="font-semibold text-[#253439]">{fmt(r.system_prompt_tokens)}</span> tok
                                <span className="text-gray-400">· once, cached</span>
                              </span>
                            )}
                          </div>

                          {turnsLoading[r.session_id] && !turnsBySession[r.session_id] ? (
                            <p className="text-xs text-gray-400 py-3">Loading turns…</p>
                          ) : (
                            <div className="rounded-xl border border-[#EBECE6] bg-white shadow-sm overflow-hidden">
                            <table className="w-full text-xs tabular-nums">
                              <thead>
                                <tr className="bg-[#F6F6F1] text-left text-[#7a847f] text-[11px] uppercase tracking-wide">
                                  <th className="px-3 py-2.5 font-semibold w-8">#</th>
                                  <th className="px-3 py-2.5 font-semibold">Time</th>
                                  <th className="px-3 py-2.5 font-semibold text-right" title="Non-cached input tokens — the user's message billed at full rate">User Msg</th>
                                  <th className="px-3 py-2.5 font-semibold text-right border-l border-[#EBECE6]" title="User message + cache write + cache read = full input context sent to the model">Total Input</th>
                                  <th className="px-3 py-2.5 font-semibold text-right">Output</th>
                                  <th className="px-3 py-2.5 font-semibold text-right border-l border-[#EBECE6]" title="Tokens served from Anthropic's cache">Cache Read</th>
                                  <th className="px-3 py-2.5 font-semibold text-right" title="Tokens written to cache this turn">Cache Write</th>
                                  <th className="px-3 py-2.5 font-semibold text-right border-l border-[#EBECE6]">Cost</th>
                                  <th className="px-3 py-2.5 font-semibold text-right">Savings</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(turnsBySession[r.session_id] || []).map((t, ti) => (
                                  <tr key={ti} className="border-t border-[#F1EFE8] bg-white transition-colors hover:bg-[#E6F0EF]/70">
                                    <td className="px-3 py-2 text-gray-400 font-medium">{t.turn}</td>
                                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap" title={fmtDate(t.ts)}>{fmtTime(t.ts)}</td>
                                    <td className={`px-3 py-2 text-right ${dimZero(t.input_tokens, "text-[#253439]")}`}>{fmt(t.input_tokens)}</td>
                                    <td className={`px-3 py-2 text-right border-l border-[#F1EFE8] font-medium ${dimZero((t.input_tokens || 0) + (t.cache_write_tokens || 0) + (t.cache_read_tokens || 0), "text-[#253439]")}`}>{fmt((t.input_tokens || 0) + (t.cache_write_tokens || 0) + (t.cache_read_tokens || 0))}</td>
                                    <td className={`px-3 py-2 text-right font-medium ${dimZero(t.output_tokens, "text-[#253439]")}`}>{fmt(t.output_tokens)}</td>
                                    <td className={`px-3 py-2 text-right border-l border-[#F1EFE8] ${dimZero(t.cache_read_tokens, "text-cyan-600")}`}>{fmt(t.cache_read_tokens)}</td>
                                    <td className={`px-3 py-2 text-right ${dimZero(t.cache_write_tokens, "text-amber-600")}`}>{fmt(t.cache_write_tokens)}</td>
                                    <td className={`px-3 py-2 text-right border-l border-[#F1EFE8] font-semibold ${dimZero(t.cost_usd, "text-[#253439]")}`}>{fmtUsd(t.cost_usd)}</td>
                                    <td className={`px-3 py-2 text-right font-medium ${dimZero(t.savings_usd, "text-emerald-600")}`}>{fmtUsd(t.savings_usd)}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="bg-white border-t-2 border-cyan-500 text-[#253439] font-bold">
                                  <td className="px-3 py-2.5 uppercase tracking-wide text-[11px] text-gray-500" colSpan={2}>Total · {r.turns} turns</td>
                                  <td className="px-3 py-2.5 text-right">{fmt(r.input_tokens)}</td>
                                  <td className="px-3 py-2.5 text-right border-l border-[#EBECE6]">{fmt((r.input_tokens || 0) + (r.cache_write_tokens || 0) + (r.cache_read_tokens || 0))}</td>
                                  <td className="px-3 py-2.5 text-right">{fmt(r.output_tokens)}</td>
                                  <td className="px-3 py-2.5 text-right border-l border-[#EBECE6] text-cyan-700">{fmt(r.cache_read_tokens)}</td>
                                  <td className="px-3 py-2.5 text-right text-amber-700">{fmt(r.cache_write_tokens)}</td>
                                  <td className="px-3 py-2.5 text-right border-l border-[#EBECE6]">{fmtUsd(r.cost_usd)}</td>
                                  <td className="px-3 py-2.5 text-right text-emerald-700">{fmtUsd(r.savings_usd)}</td>
                                </tr>
                              </tfoot>
                            </table>
                            </div>
                          )}
                          {r.api_key_name && (
                            <p className="text-xs text-gray-400 mt-2">API Key: <span className="font-medium text-gray-600">{r.api_key_name}</span>{r.api_key_id ? ` (${r.api_key_id})` : ""}</p>
                          )}
                          </div>
                            )}
                          </div>
                        </div>
                        </td>
                      </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-xs text-gray-400 mt-2 mb-6">
        {sortedSessionRows.length} session{sortedSessionRows.length === 1 ? "" : "s"}
      </p>
      </>
    );
  };

  const accentClass = viewMode === "user" ? "ring-cyan-400" : "ring-purple-400";

  return (
    <div className="flex h-screen w-full bg-[#FAFAF6] overflow-hidden">
      <AdminSideBar onLogout={onLogout} />

      {/* Main content */}
      <div className="flex-1 relative flex flex-col px-6 sm:px-10 lg:px-16 xl:px-24 py-8 overflow-auto">
        <div className="absolute top-4 right-6 z-50">
          <UserProfileMenu onLogout={onLogout} />
        </div>
        <div className="w-full max-w-[1440px] mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 pr-14">
            <div>
              <h1 className="text-2xl font-bold text-[#253439]">Token Usage & Costs</h1>
              <p className="text-gray-500 text-sm mt-1">LLM token consumption and cost tracking for TSE sessions.</p>
            </div>
            <div className="flex items-center gap-3">
              {lastUpdated && (
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  Updated {lastUpdated.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
              <button
                onClick={() => setShowCalc(true)}
                className="flex items-center gap-2 border border-cyan-500 text-cyan-600 hover:bg-cyan-50 px-4 py-2 rounded-lg text-sm font-semibold transition"
              >
                <Calculator size={15} />
                Cost Calculator
              </button>
              <button
                onClick={fetchData}
                disabled={loading}
                className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition disabled:opacity-50"
              >
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}
          {rotateMsg && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">{rotateMsg}</div>
          )}

          {/* Mode Toggle */}
          <div className="flex gap-1 mb-5 bg-[#f6f6f1] border border-[#EBECE6] rounded-xl p-1 w-fit">
            <button
              onClick={() => switchMode("user")}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${
                viewMode === "user"
                  ? "bg-cyan-500 text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-200"
              }`}
            >
              By User
            </button>
            <button
              onClick={() => switchMode("apikey")}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${
                viewMode === "apikey"
                  ? "bg-purple-500 text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-200"
              }`}
            >
              By API Key
            </button>
          </div>

          {/* Filter bar */}
          <div className="flex flex-wrap items-end gap-3 mb-6">
            {viewMode === "user" ? (
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Support Agent</label>
                <select
                  value={filterUser}
                  onChange={(e) => { setFilterUser(e.target.value); setExpandedRows(new Set()); }}
                  className={`border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-48 bg-white focus:outline-none focus:ring-2 ${accentClass}`}
                >
                  <option value="">All Users</option>
                  {byUser.map(u => (
                    <option key={u.username} value={u.username}>{u.username}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">API Key</label>
                <select
                  value={filterApiKeyName}
                  onChange={(e) => { setFilterApiKeyName(e.target.value); setExpandedRows(new Set()); }}
                  className={`border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-48 bg-white focus:outline-none focus:ring-2 ${accentClass}`}
                >
                  <option value="">All API Keys</option>
                  {byApiKey.map(k => (
                    <option key={k.api_key_name} value={k.api_key_name}>{k.api_key_name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
              />
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                (filterFrom || filterTo)
                  ? "bg-cyan-500 hover:bg-cyan-600 text-white font-semibold"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
            >
              Apply
            </button>
            <button
              onClick={() => {
                setFilterUser("");
                setFilterApiKeyName("");
                setFilterFrom("");
                setFilterTo("");
                setExpandedRows(new Set());
              }}
              className="text-gray-400 hover:text-gray-600 px-2 py-1.5 text-sm transition"
            >
              Clear
            </button>
          </div>

          {loading ? (
            <div className="text-center py-16 text-gray-400">Loading…</div>
          ) : (
            <>
              {/* Hero KPIs — the numbers an admin actually checks */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-white border border-[#EBECE6] rounded-xl p-5 shadow-sm">
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Total Cost</p>
                  <p className="text-3xl font-bold text-[#253439] mt-1.5 tabular-nums">{fmtUsd(grandCost)}</p>
                  <p className="text-[11px] text-gray-400 mt-1">{isFiltered ? "sessions only" : "incl. keepalive"}</p>
                </div>
                <div className="bg-white border border-[#EBECE6] rounded-xl p-5 shadow-sm">
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Cache Savings</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-1.5 tabular-nums">{fmtUsd(displaySummary.total_savings_usd)}</p>
                  <p className="text-[11px] text-gray-400 mt-1">vs. running uncached</p>
                </div>
                <div className="bg-white border border-[#EBECE6] rounded-xl p-5 shadow-sm">
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Keepalive Cost</p>
                  <p className="text-3xl font-bold text-amber-600 mt-1.5 tabular-nums">{fmtUsd(keepalive.cost_usd)}</p>
                  <p className="text-[11px] text-gray-400 mt-1">{keepalive.hits} hits · {keepalive.misses} miss</p>
                </div>
                <div className="bg-white border border-[#EBECE6] rounded-xl p-5 shadow-sm">
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Sessions</p>
                  <p className="text-3xl font-bold text-[#253439] mt-1.5 tabular-nums">{fmt(displaySummary.total_sessions)}</p>
                  <p className="text-[11px] text-gray-400 mt-1">{fmt(displaySummary.total_turns)} LLM turns</p>
                </div>
              </div>

              {/* Token strip — diagnostic detail, one slim card */}
              <div className="bg-white border border-[#EBECE6] rounded-xl shadow-sm mb-2 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#F1EFE8]">
                <div className="px-5 py-3.5">
                  <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide">Total Input</p>
                  <p className="text-lg font-bold text-[#253439] mt-0.5 tabular-nums">{fmt((displaySummary.total_input_tokens || 0) + (displaySummary.total_cache_write_tokens || 0) + (displaySummary.total_cache_read_tokens || 0))}</p>
                </div>
                <div className="px-5 py-3.5">
                  <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide">Output</p>
                  <p className="text-lg font-bold text-[#253439] mt-0.5 tabular-nums">{fmt(displaySummary.total_output_tokens)}</p>
                </div>
                <div className="px-5 py-3.5">
                  <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide">Cache Read</p>
                  <p className="text-lg font-bold text-cyan-600 mt-0.5 tabular-nums">{fmt(displaySummary.total_cache_read_tokens)}</p>
                </div>
                <div className="px-5 py-3.5">
                  <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide">Cache Write</p>
                  <p className="text-lg font-bold text-amber-600 mt-0.5 tabular-nums">{fmt(displaySummary.total_cache_write_tokens)}</p>
                </div>
              </div>

              {/* Collapsible legend — out of the way until needed */}
              <div className="mb-5">
                <button
                  onClick={() => setShowLegend(v => !v)}
                  className="inline-flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-cyan-600 transition"
                >
                  <Info size={12} />
                  How are these calculated?
                </button>
                {showLegend && (
                  <div className="text-xs text-gray-400 mt-2 space-y-1 bg-white border border-[#EBECE6] rounded-lg px-4 py-3">
                    <p><strong>Total Input</strong> — User Msg + Cache Write + Cache Read (everything the model actually processed). All counts are exact from Anthropic.</p>
                    <p><strong>User Msg</strong> — non-cached input tokens billed by Anthropic at full rate (user message + any uncached overhead).</p>
                    <p><strong>Cache Read</strong> — system prompt (~47K tokens) + prior conversation history served from Anthropic&apos;s cache at 0.1× rate. Refreshed every 5 min, shared across all users by content.</p>
                    <p><strong>Cache Write</strong> — new context written to Anthropic&apos;s cache this turn at 1.25× rate.</p>
                  </div>
                )}
              </div>

              {/* Breakdown Table */}
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {viewMode === "user" ? "Usage by User" : "Usage by API Key"}
              </h2>
              {viewMode === "user" ? (
                <div className="border border-[#EBECE6] rounded-xl overflow-x-auto bg-white shadow-sm mb-4">
                  {displayByUser.length === 0 ? (
                    <div className="text-center py-10 text-gray-400">No frontend usage data yet.</div>
                  ) : (
                    <table className="w-full text-sm min-w-[900px] tabular-nums">
                      <thead>
                        <tr className="bg-[#f6f6f1] text-left text-gray-600">
                          <th className="px-4 py-3 font-semibold">Username</th>
                          <th className="px-4 py-3 font-semibold text-right">Sessions</th>
                          <th className="px-4 py-3 font-semibold text-right">Turns</th>
                          <th className="px-4 py-3 font-semibold text-right">Total Input</th>
                          <th className="px-4 py-3 font-semibold text-right">Output</th>
                          <th className="px-4 py-3 font-semibold text-right">Cache Write</th>
                          <th className="px-4 py-3 font-semibold text-right">Cache Read</th>
                          <th className="px-4 py-3 font-semibold text-right">Cost</th>
                          <th className="px-4 py-3 font-semibold text-right">Savings</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayByUser.map((u, i) => (
                          <tr
                            key={u.username}
                            className={`border-t border-[#EBECE6] hover:bg-gray-50 cursor-pointer ${
                              i % 2 === 0 ? "bg-white" : "bg-[#FAFAF6]"
                            } ${filterUser === u.username ? "ring-1 ring-inset ring-cyan-300" : ""}`}
                            onClick={() => { setFilterUser(u.username); setExpandedRows(new Set()); }}
                            title={`Click to filter sessions for ${u.username}`}
                          >
                            <td className="px-4 py-3 font-medium text-[#253439]">{u.username}</td>
                            <td className="px-4 py-3 text-right">{fmt(u.sessions)}</td>
                            <td className="px-4 py-3 text-right">{fmt(u.turns)}</td>
                            <td className="px-4 py-3 text-right">{fmt((u.input_tokens || 0) + (u.cache_write_tokens || 0) + (u.cache_read_tokens || 0))}</td>
                            <td className="px-4 py-3 text-right">{fmt(u.output_tokens)}</td>
                            <td className="px-4 py-3 text-right text-orange-500">{fmt(u.cache_write_tokens)}</td>
                            <td className="px-4 py-3 text-right text-blue-500">{fmt(u.cache_read_tokens)}</td>
                            <td className="px-4 py-3 text-right font-semibold">{fmtUsd(u.cost_usd)}</td>
                            <td className="px-4 py-3 text-right text-green-600">{fmtUsd(u.savings_usd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ) : (
                <div className="border border-[#EBECE6] rounded-xl overflow-x-auto bg-white shadow-sm mb-4">
                  {displayByApiKey.length === 0 ? (
                    <div className="text-center py-10 text-gray-400">No API key usage data yet.</div>
                  ) : (
                    <table className="w-full text-sm min-w-[1000px]">
                      <thead>
                        <tr className="bg-[#f6f6f1] text-left text-gray-600">
                          <th className="px-4 py-3 font-semibold">API Key Name</th>
                          <th className="px-4 py-3 font-semibold">API Key ID</th>
                          <th className="px-4 py-3 font-semibold">User</th>
                          <th className="px-4 py-3 font-semibold text-right">Sessions</th>
                          <th className="px-4 py-3 font-semibold text-right">Turns</th>
                          <th className="px-4 py-3 font-semibold text-right">Total Input</th>
                          <th className="px-4 py-3 font-semibold text-right">Output</th>
                          <th className="px-4 py-3 font-semibold text-right">Cache Write</th>
                          <th className="px-4 py-3 font-semibold text-right">Cache Read</th>
                          <th className="px-4 py-3 font-semibold text-right">Cost</th>
                          <th className="px-4 py-3 font-semibold text-right">Savings</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayByApiKey.map((a, i) => (
                          <tr
                            key={`${a.api_key_name}-${a.owner_user_id}`}
                            className={`border-t border-[#EBECE6] hover:bg-gray-50 cursor-pointer ${
                              i % 2 === 0 ? "bg-white" : "bg-[#FAFAF6]"
                            } ${filterApiKeyName === a.api_key_name ? "ring-1 ring-inset ring-purple-300" : ""}`}
                            onClick={() => { setFilterApiKeyName(a.api_key_name); setExpandedRows(new Set()); }}
                            title={`Click to filter sessions for ${a.api_key_name}`}
                          >
                            <td className="px-4 py-3 font-medium text-[#253439]">{a.api_key_name || "—"}</td>
                            <td className="px-4 py-3 font-mono text-xs text-gray-500" title={a.api_key_id || ""}>
                              {a.api_key_id || "—"}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {a.owner_user_id ? (userIdMap[a.owner_user_id] || a.owner_user_id.slice(0, 8) + "…") : "—"}
                            </td>
                            <td className="px-4 py-3 text-right">{fmt(a.sessions)}</td>
                            <td className="px-4 py-3 text-right">{fmt(a.turns)}</td>
                            <td className="px-4 py-3 text-right">{fmt((a.input_tokens || 0) + (a.cache_write_tokens || 0) + (a.cache_read_tokens || 0))}</td>
                            <td className="px-4 py-3 text-right">{fmt(a.output_tokens)}</td>
                            <td className="px-4 py-3 text-right text-orange-500">{fmt(a.cache_write_tokens)}</td>
                            <td className="px-4 py-3 text-right text-blue-500">{fmt(a.cache_read_tokens)}</td>
                            <td className="px-4 py-3 text-right font-semibold">{fmtUsd(a.cost_usd)}</td>
                            <td className="px-4 py-3 text-right text-green-600">{fmtUsd(a.savings_usd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Session Log — always shown, scoped to current mode */}
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Session Log</h2>
              {renderSessionLog()}

              {/* Data Cleanup */}
              <div className="bg-white border border-[#EBECE6] rounded-xl p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-[#253439]">Delete Old Records</p>
                    <p className="text-xs text-gray-400 mt-1">Permanently delete token usage records older than the selected period.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {[30, 90, 180].map((d) => (
                      <button
                        key={d}
                        onClick={() => handleRotate(d)}
                        disabled={rotateLoading}
                        className="flex items-center gap-1 bg-red-50 hover:bg-red-100 text-red-600 px-3 py-1.5 rounded-lg text-xs font-medium border border-red-100 transition disabled:opacity-50"
                      >
                        <Trash2 size={11} /> &gt;{d}d
                      </button>
                    ))}
                    {rotateLoading && <span className="text-xs text-gray-400 ml-1">Deleting…</span>}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Cost Calculator Modal */}
      {showCalc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowCalc(false)}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#EBECE6] bg-[#f6f6f1]">
              <div className="flex items-center gap-2">
                <Calculator size={18} className="text-amber-500" />
                <h2 className="text-base font-bold text-[#253439]">Cost Calculator</h2>
              </div>
              <button onClick={() => setShowCalc(false)} className="text-gray-400 hover:text-gray-600 transition">
                <X size={18} />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Model selector */}
              <div className="flex items-center gap-3">
                <label className="text-xs font-semibold text-gray-500 w-20 shrink-0">Model</label>
                <select
                  value={calcModel}
                  onChange={(e) => handleCalcModelChange(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                >
                  {Object.keys(calcModels).map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <button
                  onClick={() => setCalcTokens({
                    input: displaySummary.total_input_tokens || "",
                    cache_write: displaySummary.total_cache_write_tokens || "",
                    cache_read: displaySummary.total_cache_read_tokens || "",
                    output: displaySummary.total_output_tokens || "",
                  })}
                  className="shrink-0 text-xs bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 px-3 py-1.5 rounded-lg font-medium transition whitespace-nowrap"
                  title="Fill token quantities from the currently filtered view"
                >
                  Use current view
                </button>
              </div>

              <div className="grid grid-cols-2 gap-5">
                {/* Rates */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Rates (USD / 1M tokens)</p>
                  <div className="space-y-2">
                    {[
                      { key: "input",       label: "Input (plain)",   color: "text-gray-700" },
                      { key: "cache_write", label: "Cache Write",     color: "text-orange-500" },
                      { key: "cache_read",  label: "Cache Read",      color: "text-blue-500" },
                      { key: "output",      label: "Output",          color: "text-purple-600" },
                    ].map(({ key, label, color }) => (
                      <div key={key} className="flex items-center gap-2">
                        <span className={`text-xs w-28 shrink-0 ${color} font-medium`}>{label}</span>
                        <div className="relative flex-1">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={calcRates[key]}
                            onChange={(e) => setCalcRates(r => ({ ...r, [key]: parseFloat(e.target.value) || 0 }))}
                            className="w-full border border-gray-200 rounded-lg pl-6 pr-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Token quantities */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Token Quantities</p>
                  <div className="space-y-2">
                    {[
                      { key: "input",       label: "Input (plain)",   color: "text-gray-700" },
                      { key: "cache_write", label: "Cache Write",     color: "text-orange-500" },
                      { key: "cache_read",  label: "Cache Read",      color: "text-blue-500" },
                      { key: "output",      label: "Output",          color: "text-purple-600" },
                    ].map(({ key, label, color }) => (
                      <div key={key} className="flex items-center gap-2">
                        <span className={`text-xs w-28 shrink-0 ${color} font-medium`}>{label}</span>
                        <input
                          type="number"
                          min="0"
                          placeholder="0"
                          value={calcTokens[key]}
                          onChange={(e) => setCalcTokens(t => ({ ...t, [key]: e.target.value }))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Cost breakdown */}
              <div className="bg-[#f6f6f1] border border-[#EBECE6] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#EBECE6] text-gray-500 text-xs">
                      <th className="px-4 py-2 text-left font-semibold">Token Type</th>
                      <th className="px-4 py-2 text-right font-semibold">Tokens</th>
                      <th className="px-4 py-2 text-right font-semibold">Rate / 1M</th>
                      <th className="px-4 py-2 text-right font-semibold">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: "input",       label: "Input (plain)",   tokens: parseFloat(calcTokens.input)||0,       cost: calcCost.inp_cost, rate: calcRates.input,       color: "text-gray-700" },
                      { key: "cache_write", label: "Cache Write",     tokens: parseFloat(calcTokens.cache_write)||0, cost: calcCost.cw_cost,  rate: calcRates.cache_write, color: "text-orange-500" },
                      { key: "cache_read",  label: "Cache Read",      tokens: parseFloat(calcTokens.cache_read)||0,  cost: calcCost.cr_cost,  rate: calcRates.cache_read,  color: "text-blue-500" },
                      { key: "output",      label: "Output",          tokens: parseFloat(calcTokens.output)||0,      cost: calcCost.out_cost, rate: calcRates.output,      color: "text-purple-600" },
                    ].map(({ key, label, tokens, cost, rate, color }, i) => (
                      <tr key={key} className={i % 2 === 0 ? "bg-white" : "bg-[#fafaf6]"}>
                        <td className={`px-4 py-2 font-medium text-xs ${color}`}>{label}</td>
                        <td className="px-4 py-2 text-right text-xs">{tokens.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right text-xs text-gray-400">${rate.toFixed(2)}</td>
                        <td className="px-4 py-2 text-right text-xs font-semibold">{fmtUsd(cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[#EBECE6] bg-white">
                      <td colSpan={3} className="px-4 py-3 text-sm font-bold text-[#253439]">Total Cost (with cache)</td>
                      <td className="px-4 py-3 text-right text-base font-bold text-[#253439]">{fmtUsd(calcCost.total)}</td>
                    </tr>
                    <tr className="bg-green-50">
                      <td colSpan={3} className="px-4 py-2 text-xs text-green-700 font-medium">Without cache (all input at full rate)</td>
                      <td className="px-4 py-2 text-right text-xs font-semibold text-green-700">{fmtUsd(calcCost.without_cache)}</td>
                    </tr>
                    <tr className="bg-green-50">
                      <td colSpan={3} className="px-4 py-2 text-xs text-green-600">Cache savings</td>
                      <td className="px-4 py-2 text-right text-xs font-bold text-green-600">{fmtUsd(Math.max(0, calcCost.without_cache - calcCost.total))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTokenUsagePage;
